import ContentPage from "../templates/ContentPage";
import HubTemplate from "../templates/HubTemplate";
import Layout from "../components/layout/Layout";
import SEO, { orgSchema, webPageSchema } from "../components/layout/SEO";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Reveal } from "../components/shared/Reveal";
import CaseReviewSection from "../components/home/CaseReviewSection";
import CTAButton from "../components/shared/CTAButton";
import { GUIDE_ROUTES } from "../data/guides";
import { PAGES } from "../content/pagesContent";
import { isTranslated } from "../data/localizedRoutes";
import { SITE } from "../data/site";
import { Mail, Phone, MessageCircle } from "lucide-react";
import { useI18n } from "../i18n";

// /guides hub (English only for now; FR/DE stay noindex)
export function GuidesHub() {
  return (
    <HubTemplate
      eyebrow="Guides"
      title="Patient guides"
      intro="Balanced, responsible guides to help you understand safety, cost, recovery and how to choose a surgeon before you travel."
      path="/guides"
      items={GUIDE_ROUTES.map((g) => ({ to: `/${g.slug}`, title: g.title }))}
    />
  );
}

// /patient-journey
export function PatientJourneyPage() {
  const { lang } = useI18n();
  const p = (PAGES[lang] || PAGES.en).patientJourney;
  const noindex = lang !== "en" && !isTranslated("/patient-journey", lang);
  return (
    <Layout>
      <SEO title={`${p.heading} | ${SITE.name}`} description={p.note} path="/patient-journey" noindex={noindex} schema={[orgSchema(), webPageSchema({ name: p.heading, description: p.note, path: "/patient-journey" })]} />
      <Breadcrumbs items={[{ name: p.eyebrow }]} />
      <section className="container-psv py-12 md:py-20">
        <Reveal className="max-w-3xl">
          <span className="eyebrow mb-4 block">{p.eyebrow}</span>
          <h1 className="font-serif font-medium text-primary text-5xl md:text-6xl leading-tight tracking-tight">{p.heading}</h1>
        </Reveal>
        <div className="mt-12 max-w-3xl">
          {p.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.04} className="flex items-start gap-5 py-5 border-b border-border">
              <span className="font-serif text-3xl text-accent tabular-nums w-12 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-sans text-lg md:text-xl text-primary pt-1">{s}</span>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 max-w-3xl bg-surface border-l-2 border-accent p-6 rounded-r-lg">
          <p className="text-[15px] text-secondary-foreground leading-relaxed">{p.note}</p>
        </Reveal>
      </section>
      <CaseReviewSection />
    </Layout>
  );
}

// /about
export function AboutPage() {
  const { lang } = useI18n();
  const p = (PAGES[lang] || PAGES.en).about;
  return <ContentPage eyebrow={p.eyebrow} title={p.title} intro={p.intro} path="/about" sections={p.sections} />;
}

// /editorial-policy
export function EditorialPolicy() {
  const { lang } = useI18n();
  const p = (PAGES[lang] || PAGES.en).editorial;
  return <ContentPage eyebrow={p.eyebrow} title={p.title} intro={p.intro} path="/editorial-policy" sections={p.sections} />;
}

// /contact
export function ContactPage() {
  const { t, lang } = useI18n();
  const p = (PAGES[lang] || PAGES.en).contact;
  const noindex = lang !== "en" && !isTranslated("/contact", lang);
  return (
    <Layout>
      <SEO title={`${p.title} | ${SITE.name}`} description={p.intro} path="/contact" noindex={noindex} schema={[orgSchema(), webPageSchema({ name: p.title, description: p.intro, path: "/contact" })]} />
      <Breadcrumbs items={[{ name: p.eyebrow }]} />
      <section className="container-psv py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="eyebrow mb-4 block">{p.eyebrow}</span>
            <h1 className="font-serif font-medium text-primary text-5xl md:text-6xl leading-tight tracking-tight">{p.title}</h1>
            <p className="mt-6 text-lg text-secondary-foreground font-light leading-relaxed">{p.intro}</p>
            <div className="mt-10 space-y-5">
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 group" data-testid="contact-email">
                <span className="w-11 h-11 rounded-full border border-accent/40 flex items-center justify-center"><Mail className="w-5 h-5 text-accent" /></span>
                <span className="text-[15px] text-primary group-hover:text-accent transition-colors">{SITE.email}</span>
              </a>
              <a href={SITE.phoneHref} className="flex items-center gap-4 group" data-testid="contact-phone">
                <span className="w-11 h-11 rounded-full border border-accent/40 flex items-center justify-center"><Phone className="w-5 h-5 text-accent" /></span>
                <span className="text-[15px] text-primary group-hover:text-accent transition-colors">{SITE.phone}</span>
              </a>
              <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="contact-whatsapp">
                <span className="w-11 h-11 rounded-full border border-accent/40 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-accent" /></span>
                <span className="text-[15px] text-primary group-hover:text-accent transition-colors">WhatsApp — {SITE.whatsapp}</span>
              </a>
            </div>
            <div className="mt-10">
              <CTAButton to="/#case-review" testId="contact-case-review-btn">{t("cta.caseReview")}</CTAButton>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1} className="relative overflow-hidden rounded-[2rem] grain h-full min-h-[420px] border border-border">
            <img src="https://images.unsplash.com/photo-1528360983277-13d401cdc186?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Vietnam landscape" className="w-full h-full object-cover" loading="lazy" />
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
